import type {
  DeselectEquipmentForHaulActionInput,
  DeselectEquipmentForHaulActionOutput,
  GetRecommendedEquipmentInput,
  GetRecommendedEquipmentOutput,
} from "@amzn/yt-yard360-service-endpoint-ts-client";

import { APIS } from "@/app/app-config";
import type {
  CreateGoalRequest,
  CreateGoalResponse,
  UpdateGoalRequest,
  UpdateGoalResponse,
  RequestCancelGoalRequest,
  RequestCancelGoalResponse,
  NormalizedRequestCancelGoalResponse,
  RequestHoldGoalRequest,
  RequestHoldGoalResponse,
  NormalizedRequestHoldGoalResponse,
  RequestReleaseGoalRequest,
  RequestReleaseGoalResponse,
  NormalizedRequestReleaseGoalResponse,
  GetYardGoalsRequest,
  GetYardGoalsResponse,
  GetYardAppointmentsRequest,
  GetYardAppointmentsResponse,
  GetYardEventsRequest,
  GetYardEventsResponse,
} from "@/features/ryteTracker/types";
import type { Goal } from "@/features/ryteTracker/types/goal";
import { assertNotTempGoalId } from "@/features/ryteTracker/utils/guards/assertNotTempGoalId";
import { narrowDemandIdForWrite } from "@/features/ryteTracker/utils/identity/resolveDemandId";
import {
  executeGetYardGoalsQuery,
  type GetYardGoalsBaseQuery,
} from "@/features/ryteTracker/services/getYardGoalsFanout";
import { api } from "@/services/api";
import { TagTypes } from "@/services/tagTypes";

export interface EquipmentRecommendationItem {
  visitId: string;
  equipmentId?: string;
  equipmentNumber?: string;
  equipmentType?: string;
  ownerCode?: string;
  operator?: string;
  licensePlate?: string;
  licensePlateState?: string;
  licensePlateCountry?: string;
  locationCode?: string;
  locationType?: string;
}

// Keep the local structured shape stable across generated-client revisions.
// Runtime validation still guards untyped network payloads before presentation.
export type GetRecommendedEquipmentResponse = Omit<
  GetRecommendedEquipmentOutput,
  | "selectedEquipmentVisitId"
  | "eligibleEquipmentVisitIds"
  | "loadableEquipmentVisitIds"
  | "selectedEquipment"
  | "eligibleEquipment"
  | "loadableEquipment"
> & {
  selectedEquipment?: EquipmentRecommendationItem;
  eligibleEquipment: EquipmentRecommendationItem[] | undefined;
  loadableEquipment: EquipmentRecommendationItem[] | undefined;
};

export interface OverrideRecommendationInput {
  recommendationId: string;
  equipmentId: string;
  overrideReason: string;
}

export interface OverrideRecommendationOutput {
  recommendationId: string;
}

/**
 * Normalized shape of a goal write success response.
 *
 * The write-endpoint Lambda envelope is inconsistent:
 *   - observed createYardGoal/updateYardGoal: envelope.data IS the Goal directly
 *   - legacy/getYardGoals-style: envelope.data = { goal: Goal }
 *
 * `transformResponse` below folds both shapes into `{ goal: Goal | null }`
 * so UI consumers never touch the envelope. `goal === null` means the server
 * returned a malformed / unrecognizable success payload — the caller should
 * treat that as a failure rather than silently succeeding.
 */
export interface NormalizedCreateGoalResponse {
  goal: Goal | null;
}

/**
 * Extract the Goal from a createGoal/updateGoal response envelope.
 *
 * Probes both observed envelope shapes. Returns `null` when neither matches so
 * callers can distinguish "server succeeded but payload is unusable" from
 * "server returned a well-formed Goal".
 *
 * Exported for direct unit testing of the envelope-normalization branches.
 * Production callers go through `transformResponse` on both write endpoints.
 */
export const extractGoalFromWriteResponse = (raw: unknown): Goal | null => {
  if (!raw || typeof raw !== "object") return null;
  const envelope = (raw as { goal?: unknown }).goal;
  if (!envelope || typeof envelope !== "object") return null;
  const rawData = (envelope as { data?: unknown }).data;
  if (!rawData || typeof rawData !== "object") return null;

  const dataRecord = rawData as Record<string, unknown>;
  // Direct write shape: data IS the Goal.
  if (dataRecord.goalId && typeof dataRecord.goalId === "object") {
    return dataRecord as unknown as Goal;
  }
  // Probe 2 — getYardGoals shape: data.goal IS the Goal.
  const wrapped = dataRecord.goal;
  if (
    wrapped &&
    typeof wrapped === "object" &&
    (wrapped as Record<string, unknown>).goalId &&
    typeof (wrapped as Record<string, unknown>).goalId === "object"
  ) {
    return wrapped as Goal;
  }
  return null;
};

/**
 * Extract the Goal from a cancel/hold/release mutation response envelope.
 *
 * Unlike the tolerant create/update write normalizer, this helper intentionally
 * accepts only the direct `{ goal: { data: Goal, ... } }` mutation shape and
 * also leaves each endpoint's accepted flag to its transform.
 *
 * Returns `null` when the envelope is missing, malformed, or lacks a
 * recognizable Goal payload — caller should treat that as a failure
 * rather than dispatching wrapper-shaped garbage into the slice.
 *
 * Exported for direct unit testing of the unwrap branches.
 */
export const extractGoalFromMutationResponse = (raw: unknown): Goal | null => {
  if (!raw || typeof raw !== "object") return null;
  const envelope = (raw as { goal?: unknown }).goal;
  if (!envelope || typeof envelope !== "object") return null;
  const rawData = (envelope as { data?: unknown }).data;
  if (!rawData || typeof rawData !== "object") return null;

  const dataRecord = rawData as Record<string, unknown>;
  // Single-nested cancel/hold/release shape: data IS the Goal directly.
  if (dataRecord.goalId && typeof dataRecord.goalId === "object") {
    return dataRecord as unknown as Goal;
  }
  return null;
};

/**
 * Prepare every createGoal request at the API boundary. Demand identity is
 * rebuilt as locationPlanId-first, then VRID, then ISA, with visitId retained
 * whenever known — the plan id is the planning layer's own handle and outranks
 * a re-attachable haul association on the write path (the READ precedence in
 * `resolveDemandId` still ranks VRID first; the two are independent by design).
 *
 * Plain creates omit `failureMode` for every intent and use the engine default
 * delayed-state behavior. Callers needing an exact policy (the IB Door
 * Transfer G1/G2/G3 chain, where only G3 carries FAIL_IF_DELAYED) use the
 * `failureModePolicy: "caller-specified"` wrapper so the supplied value is
 * preserved verbatim.
 *
 * Applied centrally in the createGoal `query` (not at the ~11 create call
 * sites) so present and future ordinary paths use the same policy. Exported for
 * direct unit testing of the merge contract.
 */
export const prepareCreateGoalRequest = (body: CreateGoalRequest): CreateGoalRequest => ({
  ...body,
  demandId: narrowDemandIdForWrite(body.demandId) ?? body.demandId,
  parameters: {
    ...body.parameters,
    failureMode: undefined,
  },
});

/**
 * Discriminated union for createGoal mutation input. Allows callers to
 * either pass a plain CreateGoalRequest (failureMode omitted by
 * prepareCreateGoalRequest) or wrap it with an
 * explicit `failureModePolicy: "caller-specified"` to preserve the exact
 * failureMode on request.parameters as-is.
 *
 * The `failureModePolicy` field is frontend-only metadata — it MUST NOT
 * serialize to the wire. The createGoal query normalizes the union and
 * strips it before building the HTTP body.
 */
export type CreateGoalMutationInput =
  | CreateGoalRequest
  | {
      request: CreateGoalRequest;
      failureModePolicy: "caller-specified";
    };

/** Type guard: is the input a caller-specified policy wrapper? */
const isCallerSpecifiedInput = (
  input: CreateGoalMutationInput
): input is { request: CreateGoalRequest; failureModePolicy: "caller-specified" } =>
  typeof input === "object" &&
  input !== null &&
  "failureModePolicy" in input &&
  (input as { failureModePolicy: unknown }).failureModePolicy === "caller-specified";

/** Normalize create-goal input while honoring its explicit failure-policy boundary. */
export const prepareCreateGoalMutationInput = (
  input: CreateGoalMutationInput
): CreateGoalRequest => {
  if (isCallerSpecifiedInput(input)) {
    const request = input.request;
    return {
      ...request,
      demandId: narrowDemandIdForWrite(request.demandId) ?? request.demandId,
    };
  }
  return prepareCreateGoalRequest(input);
};

/** Internal base-query marker: serialize requester as `ryte-ui:::[login]:::[activeAccountName]`. */
const RYTE_REQUESTER_IDENTITY_ENRICHMENT = { appendRequesterIdentity: true as const };

/** Build the create-goal wire descriptor after stripping frontend-only policy metadata. */
export const buildCreateGoalQuery = (input: CreateGoalMutationInput) => ({
  apiName: APIS.CreateYardGoal.name,
  url: APIS.CreateYardGoal.resource,
  method: "POST" as const,
  body: { request: prepareCreateGoalMutationInput(input) },
  ...RYTE_REQUESTER_IDENTITY_ENRICHMENT,
});

export const buildGetRecommendedEquipmentQuery = (body: GetRecommendedEquipmentInput) => ({
  apiName: APIS.GetRecommendedEquipment.name,
  url: APIS.GetRecommendedEquipment.resource,
  method: "POST" as const,
  body,
});

export const buildOverrideRecommendationQuery = (body: OverrideRecommendationInput) => ({
  apiName: APIS.OverrideRecommendation.name,
  url: APIS.OverrideRecommendation.resource,
  method: "POST" as const,
  body,
});

export const buildDeselectEquipmentQuery = (body: DeselectEquipmentForHaulActionInput) => ({
  apiName: APIS.DeselectEquipmentForHaulAction.name,
  url: APIS.DeselectEquipmentForHaulAction.resource,
  method: "POST" as const,
  body,
});

/**
 * RYTE Tracker API endpoints.
 *
 * All goal operations use real backend APIs (Smithy contracts).
 * Appointment endpoint populates the right-side panel.
 *
 * Injected into the shared RTK Query api instance so they share
 * the same cache, middleware, and auth headers as RYC endpoints.
 */
export const ryteApis = api.injectEndpoints({
  endpoints: (builder) => ({
    // ─── Goal Queries ──────────────────────────────────────────────────

    /**
     * Fetch goals for a yard by time range — Smithy: POST /getYardGoals.
     *
     * Same pattern as getYardAppointments: direct input, no request wrapper.
     * Input: { yardId: string, startEpochMs: number, endEpochMs: number }
     * Output: { goals: Goal[] }
     */
    getYardGoals: builder.query<GetYardGoalsResponse, GetYardGoalsRequest>({
      queryFn: (body, queryApi, _extraOptions, baseQuery) =>
        executeGetYardGoalsQuery(body, queryApi, baseQuery as GetYardGoalsBaseQuery),
      providesTags: [TagTypes.RyteGoals],
    }),

    getRecommendedEquipment: builder.mutation<
      GetRecommendedEquipmentResponse,
      GetRecommendedEquipmentInput
    >({
      query: buildGetRecommendedEquipmentQuery,
    }),

    overrideRecommendation: builder.mutation<
      OverrideRecommendationOutput,
      OverrideRecommendationInput
    >({
      query: buildOverrideRecommendationQuery,
    }),

    deselectEquipmentForHaulAction: builder.mutation<
      DeselectEquipmentForHaulActionOutput,
      DeselectEquipmentForHaulActionInput
    >({
      query: buildDeselectEquipmentQuery,
    }),

    // ─── Goal Mutations ────────────────────────────────────────────────

    /** Create a new goal (drag from queue → timeline) — Smithy: POST /createYardGoal.
     *
     * The raw response envelope is normalized in-flight via `transformResponse`
     * so callers receive `{ goal: Goal | null }` directly. A `null` goal means
     * the server returned 200 with an unrecognizable payload — treat it as a
     * failure rather than silently succeeding.
     *
     * Accepts either a plain CreateGoalRequest (failureMode omitted)
     * or a `{ request, failureModePolicy: "caller-specified" }` wrapper that
     * preserves the exact failureMode on the request.
     * The wrapper is stripped before serialization — it never reaches the wire.
     */
    createGoal: builder.mutation<NormalizedCreateGoalResponse, CreateGoalMutationInput>({
      query: buildCreateGoalQuery,
      transformResponse: (raw: CreateGoalResponse): NormalizedCreateGoalResponse => ({
        goal: extractGoalFromWriteResponse(raw),
      }),
    }),

    /**
     * Update goal parameters (drag to reschedule, resize pill) — Smithy: POST /updateYardGoal.
     *
     * Production normally returns the Goal directly under `goal.data`; legacy
     * responses may use `goal.data.goal`. The shared write normalizer accepts
     * both so an HTTP-200 mutation cannot be misreported as malformed solely
     * because the envelope variant changed.
     */
    updateGoal: builder.mutation<NormalizedCreateGoalResponse, UpdateGoalRequest>({
      query: (body) => {
        // Refuse to send FE-synthesized optimistic temp goalIds to the engine.
        // Spec: `.claude/specs/optimistic-temp-remove-seed/` Req 5.
        assertNotTempGoalId(body.goalId.id, "updateGoal");
        return {
          apiName: APIS.UpdateYardGoal.name,
          url: APIS.UpdateYardGoal.resource,
          method: "POST",
          body: { request: body },
          ...RYTE_REQUESTER_IDENTITY_ENRICHMENT,
        };
      },
      transformResponse: (raw: UpdateGoalResponse): NormalizedCreateGoalResponse => ({
        goal: extractGoalFromWriteResponse(raw),
      }),
    }),

    /**
     * Request async cancel of a goal — Smithy: POST /requestCancelYardGoal.
     *
     * Wire returns `{ goal: { data: Goal, goalId, yardId }, cancelRequestAccepted }`
     * — the same Lambda envelope `getYardGoals` uses. `transformResponse`
     * unwraps to `{ goal: Goal | null, cancelRequestAccepted }` so consumers
     * receive a real Goal record (matches `applyGoalFromMutation`'s contract).
     * Without this unwrap, dispatching `res.goal` straight into `upsertGoal`
     * pollutes the slice with wrapper-shaped entries, and any subsequent
     * `resolveAllDemandIds(goal.demandId)` crashes — `demandId` lives at
     * `wrapper.data.demandId`, not on the wrapper itself.
     */
    requestCancelGoal: builder.mutation<
      NormalizedRequestCancelGoalResponse,
      RequestCancelGoalRequest
    >({
      query: (body) => {
        assertNotTempGoalId(body.goalId.id, "requestCancelGoal");
        return {
          apiName: APIS.RequestCancelYardGoal.name,
          url: APIS.RequestCancelYardGoal.resource,
          method: "POST",
          body: { request: body },
          ...RYTE_REQUESTER_IDENTITY_ENRICHMENT,
        };
      },
      transformResponse: (raw: RequestCancelGoalResponse): NormalizedRequestCancelGoalResponse => ({
        goal: extractGoalFromMutationResponse(raw),
        cancelRequestAccepted: raw?.cancelRequestAccepted ?? false,
      }),
    }),

    /** Request async hold of a goal — Smithy: POST /requestHoldYardGoal. Same wire-envelope pattern as requestCancelGoal. */
    requestHoldGoal: builder.mutation<NormalizedRequestHoldGoalResponse, RequestHoldGoalRequest>({
      query: (body) => {
        assertNotTempGoalId(body.goalId.id, "requestHoldGoal");
        return {
          apiName: APIS.RequestHoldYardGoal.name,
          url: APIS.RequestHoldYardGoal.resource,
          method: "POST",
          body: { request: body },
          ...RYTE_REQUESTER_IDENTITY_ENRICHMENT,
        };
      },
      transformResponse: (raw: RequestHoldGoalResponse): NormalizedRequestHoldGoalResponse => ({
        goal: extractGoalFromMutationResponse(raw),
        holdRequestAccepted: raw?.holdRequestAccepted ?? false,
      }),
    }),

    /** Request async release of a held goal — Smithy: POST /requestReleaseYardGoal. Same wire-envelope pattern as requestCancelGoal. */
    requestReleaseGoal: builder.mutation<
      NormalizedRequestReleaseGoalResponse,
      RequestReleaseGoalRequest
    >({
      query: (body) => {
        assertNotTempGoalId(body.goalId.id, "requestReleaseGoal");
        return {
          apiName: APIS.RequestReleaseYardGoal.name,
          url: APIS.RequestReleaseYardGoal.resource,
          method: "POST",
          body: { request: body },
          ...RYTE_REQUESTER_IDENTITY_ENRICHMENT,
        };
      },
      transformResponse: (
        raw: RequestReleaseGoalResponse
      ): NormalizedRequestReleaseGoalResponse => ({
        goal: extractGoalFromMutationResponse(raw),
        releaseRequestAccepted: raw?.releaseRequestAccepted ?? false,
      }),
    }),

    // ─── Right Panel Data ──────────────────────────────────────────────

    /**
     * Fetch appointments — scheduled trailer arrivals.
     *
     * Smithy contract: POST /getYardAppointments
     * Input: { buildingCode, startEpochMs, endEpochMs, view: "RYTE" }
     * Output: { appointments: YardAppointment[] }
     */
    getYardAppointments: builder.query<GetYardAppointmentsResponse, GetYardAppointmentsRequest>({
      query: (body) => ({
        apiName: APIS.FetchRyteAppointments.name,
        url: APIS.FetchRyteAppointments.resource,
        method: "POST",
        body,
      }),
      providesTags: [TagTypes.RyteAppointments],
    }),

    // ─── Events ────────────────────────────────────────────────────────

    /**
     * Fetch yard events — Smithy: POST /getYardEvents.
     *
     * Supports filtering by yardId, visitId, eventTypes, and time range.
     * Input: { yardId?, visitId?, eventTypes?, startEpochMs, endEpochMs }
     * Output: { events: YardEventEnvelope[] }
     */
    getYardEvents: builder.query<GetYardEventsResponse, GetYardEventsRequest>({
      query: (body) => ({
        apiName: APIS.GetYardEvents.name,
        url: APIS.GetYardEvents.resource,
        method: "POST",
        body,
      }),
      providesTags: [TagTypes.RyteEvents],
    }),
  }),
});

export const {
  // Goal queries
  useGetYardGoalsQuery,
  useGetRecommendedEquipmentMutation,
  useOverrideRecommendationMutation,
  useDeselectEquipmentForHaulActionMutation,
  // Goal mutations
  useCreateGoalMutation,
  useUpdateGoalMutation,
  useRequestCancelGoalMutation,
  useRequestHoldGoalMutation,
  useRequestReleaseGoalMutation,
  // Panel data
  useGetYardAppointmentsQuery,
  // Events
  useGetYardEventsQuery,
} = ryteApis;
